import { useEffect, useState } from 'react';

/**
 * Port of the old panel's `TimeCountDown` — the live elapsed/remaining timer
 * used by the debt countdowns and the stage-8 waiting timer.
 *
 * Faithful quirks, all from the source:
 * - The SIGN is inverted: while `endTime` is still in the future the output is
 *   prefixed `-`, and once it has passed the prefix flips to `+`.
 * - The format widens with magnitude: `MM:SS`, `HH:MM:SS`, `DD:HH:MM:SS`.
 * - Without an explicit `color`, the pill turns from green to yellow once
 *   `dim` (default 8) minutes have elapsed PAST `endTime`.
 * - `onlyShow` renders a STATIC diff between `doneAt` and `endTime` (no
 *   interval), which the debt-review timer uses.
 * - `onlyText` drops the pill chrome and renders plain grey text (the list's
 *   waiting-column / debt variants).
 */
export function LegacyTimeCountdown({
  endTime,
  doneAt,
  onlyShow = false,
  onlyText = false,
  differenceInMinutes,
  color,
}: {
  /** The reference moment the timer counts against. */
  endTime: string | Date;
  /** `onlyShow` only: the fixed "now" to diff against `endTime`. */
  doneAt?: string | Date;
  onlyShow?: boolean;
  onlyText?: boolean;
  /** Minutes past `endTime` before the auto color flips green → yellow. */
  differenceInMinutes?: number;
  color?: 'green' | 'yellow' | 'red';
}) {
  // Recomputed on every render, so a prop change (a new `endTime`) reflects
  // immediately without waiting for the next tick — the `tick` state below
  // exists only to force that next-second re-render, its value is unused.
  const state = compute({ endTime, doneAt, onlyShow, dim: differenceInMinutes });
  const [, setTick] = useState(0);

  useEffect(() => {
    if (onlyShow) return;
    const interval = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(interval);
  }, [onlyShow]);

  const tone = color ?? state.color;
  const toneVar = `var(--lg-${tone}-500)`;
  const toneBgVar = `var(--lg-${tone}-50)`;

  if (onlyText) {
    return (
      <span className="text-[12px] font-normal text-[var(--lg-grey-400)]">
        {state.time}
      </span>
    );
  }

  return (
    <span
      className="inline-flex w-fit items-center justify-center rounded-[12px] border px-2 py-1 text-[12px] font-normal"
      style={{ color: toneVar, borderColor: toneVar, backgroundColor: toneBgVar }}
    >
      {state.time}
    </span>
  );
}

function compute({
  endTime,
  doneAt,
  onlyShow,
  dim = 8,
}: {
  endTime: string | Date;
  doneAt?: string | Date | undefined;
  onlyShow: boolean;
  dim?: number | undefined;
}): { time: string; color: 'green' | 'yellow' } {
  const end = new Date(endTime);
  const now = onlyShow && doneAt ? new Date(doneAt) : new Date();
  const diffMs = now.getTime() - end.getTime();
  const abs = Math.abs(diffMs);

  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor(abs / 3_600_000) % 24;
  const minutes = Math.floor(abs / 60_000) % 60;
  const seconds = Math.floor(abs / 1000) % 60;
  const p = (n: number) => String(n).padStart(2, '0');

  let formatted;
  if (days > 0) formatted = `${p(days)}:${p(hours)}:${p(minutes)}:${p(seconds)}`;
  else if (hours > 0) formatted = `${p(hours)}:${p(minutes)}:${p(seconds)}`;
  else formatted = `${p(minutes)}:${p(seconds)}`;

  // Old source: `(!isNegative ? '-' : '+')` where negative = end still in the
  // future — so a FUTURE endTime prints `+` and a passed one prints `-`.
  const time = (diffMs < 0 ? '+' : '-') + formatted;
  const diffMinutes = Math.floor(diffMs / 60_000);
  return { time, color: diffMinutes >= (dim ?? 8) ? 'yellow' : 'green' };
}
